/**
 * useInsights — hooks for the Phase-1/2 insight endpoints.
 *
 *   useRefundsQueue       — /dashboard/insights/refunds-queue
 *   usePayoutQueue        — /dashboard/insights/payout-queue
 *   useCreatorConcentration — /dashboard/insights/creator-concentration?from=&to=&rangeId=
 *   useCohortRetention    — /dashboard/insights/cohort-retention
 *   useUnitEconomics      — /dashboard/insights/unit-economics
 *   useTopEarningCreators — /reports/top-earning-creators?from=&to=&limit=&minEarnings=
 *   useBrandCampaignRoi   — /reports/brand-campaign-roi?from=&to=&limit=
 *
 * No mock fallback — null + error on failure.
 */
import { useEffect, useState, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const FETCH_TIMEOUT_MS = 10000;

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => { controller.abort(); reject(new Error(`timeout after ${ms}ms`)); }, ms);
    promise.then(v => { clearTimeout(t); resolve(v); }, e => { clearTimeout(t); reject(e); });
  });
}

function makeFetcher(pathBuilder, depsKey) {
  return function useEndpoint(...args) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const path = pathBuilder(...args);
    const key = depsKey ? depsKey(...args) : path;
    const load = useCallback(async () => {
      setLoading(true); setError(null);
      const controller = new AbortController();
      try {
        const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
        const apiKey = process.env.REACT_APP_API_KEY;
        const headers = {};
        if (token)       headers.Authorization = `Bearer ${token}`;
        else if (apiKey) headers["X-API-Key"]  = apiKey;
        const res = await withTimeout(
          fetch(`${API_BASE}${path}`, {
            headers,
            signal: controller.signal,
          }),
          FETCH_TIMEOUT_MS, controller,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
        setData(json.data);
      } catch (e) {
        setError(e.message || String(e));
        setData(null);
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [key]);
    return { data, error, loading, reload: load };
  };
}

export const useRefundsQueue       = makeFetcher(() => "/dashboard/insights/refunds-queue");
export const usePayoutQueue        = makeFetcher(() => "/dashboard/insights/payout-queue");
export const useCohortRetention    = makeFetcher(() => "/dashboard/insights/cohort-retention");
export const useUnitEconomics      = makeFetcher(() => "/dashboard/insights/unit-economics");

export const useCreatorConcentration = makeFetcher(
  (range, rangeId) => {
    const p = new URLSearchParams();
    if (range?.from) p.set("from", range.from);
    if (range?.to)   p.set("to",   range.to);
    if (rangeId)     p.set("rangeId", rangeId);
    const qs = p.toString();
    return `/dashboard/insights/creator-concentration${qs ? `?${qs}` : ""}`;
  },
  (range, rangeId) => `${range?.from || ""}|${range?.to || ""}|${rangeId || ""}`,
);

export const useTopEarningCreators = makeFetcher(
  ({ from, to, limit = 50, minEarnings = 0 }) => {
    const p = new URLSearchParams();
    if (from)       p.set("from", from);
    if (to)         p.set("to",   to);
    if (limit)      p.set("limit", String(limit));
    if (minEarnings != null) p.set("minEarnings", String(minEarnings));
    return `/reports/top-earning-creators?${p}`;
  },
  ({ from, to, limit, minEarnings }) => `${from}|${to}|${limit}|${minEarnings}`,
);

export const useBrandCampaignRoi = makeFetcher(
  ({ from, to, limit = 50 }) => {
    const p = new URLSearchParams();
    if (from)  p.set("from", from);
    if (to)    p.set("to",   to);
    if (limit) p.set("limit", String(limit));
    return `/reports/brand-campaign-roi?${p}`;
  },
  ({ from, to, limit }) => `${from}|${to}|${limit}`,
);
